import React, { useState, useCallback } from 'react';
import {
  View,
  ScrollView,
  TouchableOpacity,
  StyleSheet,
  Dimensions,
  RefreshControl,
  Alert,
} from 'react-native';
import { StatusBar } from 'expo-status-bar';
import { Ionicons } from '@expo/vector-icons';
import { LinearGradient } from 'expo-linear-gradient';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useFocusEffect } from '@react-navigation/native';
import { H1, H2, H3, Body1, Body2, Caption } from '../../components/common/TextStyles';
import { getShadow } from '../../theme/useTheme';
import {
  getLMSProgress,
  getLMSSummary,
  getLastSyncTime,
  downloadTranscript,
  getDownloadedTranscripts,
  openTranscript,
} from '../../services/lmsService';
import { awardLmsCoursePoints } from '../../api/auth';

const { width: SCREEN_WIDTH } = Dimensions.get('window');

// ─── Status badge config ─────────────────────────────────────────────────────
const STATUS_CONFIG = {
  completed: { label: 'Completed', color: '#10B981', bg: '#ECFDF5', icon: 'checkmark-circle' },
  in_progress: { label: 'In Progress', color: '#6366F1', bg: '#EEF2FF', icon: 'play-circle' },
  enrolled: { label: 'Enrolled', color: '#F59E0B', bg: '#FFFBEB', icon: 'school' },
  payment_pending: { label: 'Payment Pending', color: '#EF4444', bg: '#FEF2F2', icon: 'alert-circle' },
};

const getStatusConfig = (status) =>
  STATUS_CONFIG[status] || STATUS_CONFIG.enrolled;

// ─── Helpers ─────────────────────────────────────────────────────────────────
const formatSyncTime = (isoString) => {
  if (!isoString) return 'Never synced';
  const date = new Date(isoString);
  const now = new Date();
  const diffMs = now - date;
  const diffMins = Math.floor(diffMs / 60000);
  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  const diffHrs = Math.floor(diffMins / 60);
  if (diffHrs < 24) return `${diffHrs}h ago`;
  return date.toLocaleDateString();
};

// ─── Unit Row ────────────────────────────────────────────────────────────────
const UnitRow = ({ unit, courseId, courseName, downloadedTranscripts, onDownload, onOpen }) => {
  const cfg = getStatusConfig(unit.status);
  // Look for a transcript specifically for this unit
  const existingTranscript = downloadedTranscripts.find(t => t.courseId === unit.id || t.unitId === unit.id);

  return (
    <View style={unitStyles.row}>
      <View style={[unitStyles.statusDot, { backgroundColor: cfg.color }]} />
      <View style={unitStyles.content}>
        <Body2 style={unitStyles.name}>{unit.name}</Body2>
        <View style={[unitStyles.badge, { backgroundColor: cfg.bg }]}>
          <Ionicons name={cfg.icon} size={10} color={cfg.color} />
          <Caption style={[unitStyles.badgeText, { color: cfg.color }]}>{cfg.label}</Caption>
        </View>
      </View>
      {unit.transcriptAvailable && (
        existingTranscript ? (
          <TouchableOpacity style={unitStyles.openBtn} onPress={() => onOpen(existingTranscript.localPath)}>
            <Ionicons name="document-text" size={14} color="#6366F1" />
            <Caption style={unitStyles.openTxt}>Open</Caption>
          </TouchableOpacity>
        ) : (
          <TouchableOpacity style={unitStyles.dlBtn} onPress={() => onDownload(unit, courseId, courseName)}>
            <Ionicons name="download-outline" size={14} color="#10B981" />
          </TouchableOpacity>
        )
      )}
    </View>
  );
};

const unitStyles = StyleSheet.create({
  row: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 8,
    borderBottomWidth: 1,
    borderBottomColor: '#F3F4F6',
    gap: 10,
  },
  statusDot: {
    width: 8,
    height: 8,
    borderRadius: 4,
  },
  content: {
    flex: 1,
    gap: 4,
  },
  name: {
    color: '#1F2937',
    fontSize: 13,
    fontWeight: '600',
  },
  badge: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 3,
    paddingHorizontal: 6,
    paddingVertical: 2,
    borderRadius: 5,
    alignSelf: 'flex-start',
  },
  badgeText: {
    fontSize: 9,
    fontWeight: '700',
  },
  dlBtn: {
    width: 30,
    height: 30,
    borderRadius: 15,
    backgroundColor: '#ECFDF5',
    alignItems: 'center',
    justifyContent: 'center',
  },
  openBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 3,
    backgroundColor: '#EEF2FF',
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 8,
  },
  openTxt: {
    color: '#6366F1',
    fontSize: 10,
    fontWeight: '700',
  },
});

// ─── Course Card ─────────────────────────────────────────────────────────────
const CourseCard = ({ course, downloadedTranscripts, onDownloadTranscript, onOpenTranscript, onViewInPortal }) => {
  const [expanded, setExpanded] = useState(false);
  const cfg = getStatusConfig(course.overallStatus || course.courseStatus);
  const hasUnits = course.units && course.units.length > 0;
  const progress = course.progressPercent || 0;
  const completedUnits = course.completedUnits || 0;
  const totalUnits = course.totalUnits || (course.unitsTotal) || 0;
  const courseName = course.courseName || course.title || 'Course';
  const courseId = course.courseId;

  const existingTranscript = downloadedTranscripts.find(t => t.courseId === courseId);

  return (
    <View style={cardStyles.card}>
      {/* Card Header */}
      <TouchableOpacity style={cardStyles.header} onPress={() => setExpanded(!expanded)} activeOpacity={0.8}>
        <View style={[cardStyles.iconWrap, { backgroundColor: cfg.bg }]}>
          <Ionicons name={cfg.icon} size={18} color={cfg.color} />
        </View>
        <View style={cardStyles.titleBlock}>
          <Body1 style={cardStyles.title} numberOfLines={2}>{courseName}</Body1>
          <View style={cardStyles.metaRow}>
            <View style={[cardStyles.statusBadge, { backgroundColor: cfg.bg }]}>
              <Caption style={[cardStyles.statusText, { color: cfg.color }]}>{cfg.label}</Caption>
            </View>
            {totalUnits > 0 && (
              <Caption style={cardStyles.unitCount}>
                {completedUnits}/{totalUnits} units
              </Caption>
            )}
          </View>
        </View>
        <Ionicons
          name={expanded ? 'chevron-up' : 'chevron-down'}
          size={18}
          color="#9CA3AF"
        />
      </TouchableOpacity>

      {/* Progress Bar */}
      <View style={cardStyles.progressSection}>
        <View style={cardStyles.progressBg}>
          <View style={[cardStyles.progressFill, { width: `${progress}%` }]} />
        </View>
        <Caption style={cardStyles.progressText}>{progress}%</Caption>
      </View>

      {/* Action Buttons */}
      <View style={cardStyles.actions}>
        <TouchableOpacity style={cardStyles.portalBtn} onPress={() => onViewInPortal(course)}>
          <Ionicons name="open-outline" size={12} color="#4338CA" />
          <Caption style={cardStyles.portalTxt}>View in Portal</Caption>
        </TouchableOpacity>

        {course.transcriptAvailable && (
          existingTranscript ? (
            <TouchableOpacity
              style={cardStyles.openTranscriptBtn}
              onPress={() => onOpenTranscript(existingTranscript.localPath)}
            >
              <Ionicons name="document-text" size={12} color="#FFFFFF" />
              <Caption style={cardStyles.openTranscriptTxt}>Open Transcript</Caption>
            </TouchableOpacity>
          ) : (
            <TouchableOpacity
              style={cardStyles.downloadBtn}
              onPress={() => onDownloadTranscript(course)}
            >
              <Ionicons name="download-outline" size={12} color="#FFFFFF" />
              <Caption style={cardStyles.downloadTxt}>Download Transcript</Caption>
            </TouchableOpacity>
          )
        )}
      </View>

      {/* Expanded Units List */}
      {expanded && hasUnits && (
        <View style={cardStyles.unitsContainer}>
          <Caption style={cardStyles.unitsLabel}>Unit Breakdown</Caption>
          {course.units.map((unit, idx) => (
            <UnitRow
              key={`${unit.name}-${idx}`}
              unit={unit}
              courseId={courseId}
              courseName={courseName}
              downloadedTranscripts={downloadedTranscripts}
              onDownload={onDownloadTranscript}
              onOpen={onOpenTranscript}
            />
          ))}
        </View>
      )}

      {expanded && !hasUnits && (
        <View style={cardStyles.noUnits}>
          <Caption style={cardStyles.noUnitsTxt}>
            Visit the portal to load unit details for this course.
          </Caption>
        </View>
      )}
    </View>
  );
};

const cardStyles = StyleSheet.create({
  card: {
    backgroundColor: '#FFFFFF',
    borderRadius: 16,
    marginBottom: 14,
    overflow: 'hidden',
    ...getShadow('sm'),
  },
  header: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    padding: 16,
    gap: 12,
  },
  iconWrap: {
    width: 36,
    height: 36,
    borderRadius: 10,
    alignItems: 'center',
    justifyContent: 'center',
  },
  titleBlock: {
    flex: 1,
    gap: 6,
  },
  title: {
    color: '#111827',
    fontWeight: '700',
    fontSize: 14,
    lineHeight: 20,
  },
  metaRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  statusBadge: {
    paddingHorizontal: 7,
    paddingVertical: 2,
    borderRadius: 5,
  },
  statusText: {
    fontSize: 9,
    fontWeight: '800',
  },
  unitCount: {
    color: '#9CA3AF',
    fontSize: 10,
    fontWeight: '600',
  },
  progressSection: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 16,
    paddingBottom: 12,
    gap: 10,
  },
  progressBg: {
    flex: 1,
    height: 5,
    backgroundColor: '#F3F4F6',
    borderRadius: 3,
    overflow: 'hidden',
  },
  progressFill: {
    height: '100%',
    backgroundColor: '#6366F1',
    borderRadius: 3,
  },
  progressText: {
    color: '#6B7280',
    fontSize: 10,
    fontWeight: '700',
    minWidth: 28,
    textAlign: 'right',
  },
  actions: {
    flexDirection: 'row',
    paddingHorizontal: 16,
    paddingBottom: 14,
    gap: 8,
  },
  portalBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 5,
    backgroundColor: '#EEF2FF',
    paddingHorizontal: 10,
    paddingVertical: 7,
    borderRadius: 9,
  },
  portalTxt: {
    color: '#4338CA',
    fontSize: 10,
    fontWeight: '700',
  },
  downloadBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 5,
    backgroundColor: '#10B981',
    paddingHorizontal: 10,
    paddingVertical: 7,
    borderRadius: 9,
  },
  downloadTxt: {
    color: '#FFFFFF',
    fontSize: 10,
    fontWeight: '700',
  },
  openTranscriptBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 5,
    backgroundColor: '#6366F1',
    paddingHorizontal: 10,
    paddingVertical: 7,
    borderRadius: 9,
  },
  openTranscriptTxt: {
    color: '#FFFFFF',
    fontSize: 10,
    fontWeight: '700',
  },
  unitsContainer: {
    paddingHorizontal: 16,
    paddingBottom: 14,
    borderTopWidth: 1,
    borderTopColor: '#F9FAFB',
    paddingTop: 10,
  },
  unitsLabel: {
    color: '#9CA3AF',
    fontSize: 10,
    fontWeight: '800',
    letterSpacing: 0.8,
    marginBottom: 6,
    textTransform: 'uppercase',
  },
  noUnits: {
    paddingHorizontal: 16,
    paddingBottom: 14,
    borderTopWidth: 1,
    borderTopColor: '#F9FAFB',
    paddingTop: 10,
  },
  noUnitsTxt: {
    color: '#9CA3AF',
    fontSize: 11,
    fontStyle: 'italic',
    textAlign: 'center',
  },
});

// ─── Main Screen ─────────────────────────────────────────────────────────────
const LMSProgressScreen = ({ navigation }) => {
  const insets = useSafeAreaInsets();
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [downloadingId, setDownloadingId] = useState(null);
  const [summary, setSummary] = useState({ enrolled: 0, completed: 0, certificates: 0, courses: [] });
  const [lastSync, setLastSync] = useState(null);
  const [downloadedTranscripts, setDownloadedTranscripts] = useState([]);

  const loadData = useCallback(async () => {
    const [s, syncTime, transcripts] = await Promise.all([
      getLMSSummary(),
      getLastSyncTime(),
      getDownloadedTranscripts(),
    ]);

    // Track points
    if (s && s.courses && s.courses.length > 0) {
      for (const course of s.courses) {
        if (course.overallStatus === 'completed' || course.courseStatus === 'completed') {
          try {
            const res = await awardLmsCoursePoints(course.courseId);
            if (res && res.pointsAwarded) {
              Alert.alert(
                'Achievement Unlocked! 🎓',
                `Congratulations on completing "${course.courseName || course.title}"!\nYou earned +${res.pointsAdded} XP! 🎯`
              );
            }
          } catch (e) {
            console.log('[LMSProgress] Points check failed:', e);
          }
        }
      }
    }

    setSummary(s);
    setLastSync(syncTime);
    setDownloadedTranscripts(transcripts);
    setLoading(false);
    setRefreshing(false);
  }, []);

  // Reload data every time screen is focused
  useFocusEffect(
    useCallback(() => {
      setLoading(true);
      loadData();
    }, [loadData])
  );

  const onRefresh = () => {
    setRefreshing(true);
    loadData();
  };

  const handleSyncNow = () => {
    // Refresh data from the new REST API
    setLoading(true);
    loadData();
  };

  const handleDownloadTranscript = async (item, parentCourseId = null, parentCourseName = null) => {
    // If parentCourseId is present, this is a unit transcript
    const itemId = item.courseId || item.id;
    const isUnit = !!parentCourseId;
    
    setDownloadingId(itemId);
    const result = await downloadTranscript({
      courseId: isUnit ? parentCourseId : itemId,
      unitId: isUnit ? itemId : null,
      isUnit,
      courseName: isUnit ? item.name : (item.courseName || item.title || 'Course')
    });
    setDownloadingId(null);

    if (result.success) {
      // Refresh transcript list
      const transcripts = await getDownloadedTranscripts();
      setDownloadedTranscripts(transcripts);
    } else {
      Alert.alert('Download Failed', 'Could not download the transcript. Please try again.');
    }
  };

  const handleOpenTranscript = async (localPath) => {
    await openTranscript(localPath);
  };

  const handleViewInPortal = (course) => {
    // Navigating to the working 'units' screen as done in SomaScreen, 
    // or falling back to the course screen if preferred in IntuitLMSScreen 
    navigation.navigate('IntuitLMS', { screen: 'units' });
  };

  const hasCourses = summary.courses && summary.courses.length > 0;

  return (
    <View style={styles.container}>
      <StatusBar style="light" />

      {/* Gradient Header */}
      <View style={[styles.header, { paddingTop: insets.top }]}>
        <LinearGradient colors={['#1e1b4b', '#4338ca', '#6366f1']} style={StyleSheet.absoluteFill} />
        {/* Aura blobs */}
        <View style={styles.aura1} />
        <View style={styles.aura2} />

        <View style={styles.headerContent}>
          <TouchableOpacity onPress={() => navigation.goBack()} style={styles.backBtn}>
            <Ionicons name="chevron-back" size={22} color="#FFFFFF" />
          </TouchableOpacity>
          <View>
            <Caption style={styles.headerSuper}>SOMA • LEARNING</Caption>
            <H2 style={styles.headerTitle}>My Learning Journey</H2>
          </View>
          <TouchableOpacity onPress={handleSyncNow} style={styles.syncBtn}>
            <Ionicons name="sync-outline" size={16} color="#FFFFFF" />
            <Caption style={styles.syncText}>Sync</Caption>
          </TouchableOpacity>
        </View>

        {/* Stats Row */}
        <View style={styles.statsRow}>
          <View style={styles.statPill}>
            <H3 style={styles.statNum}>{summary.requiresLogin ? '--' : summary.enrolled}</H3>
            <Caption style={styles.statLabel}>Enrolled</Caption>
          </View>
          <View style={styles.statDivider} />
          <View style={styles.statPill}>
            <H3 style={styles.statNum}>{summary.requiresLogin ? '--' : summary.completed}</H3>
            <Caption style={styles.statLabel}>Completed</Caption>
          </View>
          <View style={styles.statDivider} />
          <View style={styles.statPill}>
            <H3 style={styles.statNum}>{summary.requiresLogin ? '--' : summary.certificates}</H3>
            <Caption style={styles.statLabel}>Transcripts</Caption>
          </View>
        </View>

        {/* Last sync timestamp */}
        <View style={styles.syncRow}>
          <Ionicons name="time-outline" size={10} color="rgba(255,255,255,0.6)" />
          <Caption style={styles.syncTimestamp}>
            Last synced: {formatSyncTime(lastSync)}
          </Caption>
        </View>
      </View>

      {/* Content */}
      <ScrollView
        style={styles.scroll}
        contentContainerStyle={[styles.scrollContent, { paddingBottom: insets.bottom + 24 }]}
        showsVerticalScrollIndicator={false}
        refreshControl={
          <RefreshControl
            refreshing={refreshing}
            onRefresh={onRefresh}
            tintColor="#6366F1"
            progressViewOffset={20}
          />
        }
      >
        {loading ? (
          <View style={styles.loadingState}>
            <Ionicons name="school-outline" size={40} color="#D1D5DB" />
            <Body2 style={styles.loadingText}>Loading your progress...</Body2>
          </View>
        ) : summary.requiresLogin ? (
          /* LMS Login Required State */
          <View style={styles.emptyState}>
            <LinearGradient colors={['#EEF2FF', '#F5F3FF']} style={styles.emptyIcon}>
              <Ionicons name="lock-closed-outline" size={36} color="#6366F1" />
            </LinearGradient>
            <H3 style={styles.emptyTitle}>Portal Sync Required</H3>
            <Body2 style={styles.emptyDesc}>
              Please log in to the Intuit Learning Portal once to securely sync your progress with this app.
            </Body2>
            <TouchableOpacity 
              style={styles.emptyBtn} 
              onPress={() => navigation.navigate('IntuitLMS', { screen: 'select-institution' })}
            >
              <LinearGradient colors={['#4338ca', '#6366F1']} style={styles.emptyBtnGrad}>
                <Ionicons name="log-in-outline" size={16} color="#FFFFFF" />
                <Caption style={styles.emptyBtnText}>Login to Portal</Caption>
              </LinearGradient>
            </TouchableOpacity>
          </View>
        ) : !hasCourses ? (
          /* Empty State */
          <View style={styles.emptyState}>
            <LinearGradient colors={['#EEF2FF', '#F5F3FF']} style={styles.emptyIcon}>
              <Ionicons name="school-outline" size={36} color="#6366F1" />
            </LinearGradient>
            <H3 style={styles.emptyTitle}>No Progress Synced Yet</H3>
            <Body2 style={styles.emptyDesc}>
              Visit the Learning Portal — your course progress will automatically appear here after each visit.
            </Body2>
            <TouchableOpacity style={styles.emptyBtn} onPress={handleSyncNow}>
              <LinearGradient colors={['#4338ca', '#6366F1']} style={styles.emptyBtnGrad}>
                <Ionicons name="open-outline" size={16} color="#FFFFFF" />
                <Caption style={styles.emptyBtnText}>Open Learning Portal</Caption>
              </LinearGradient>
            </TouchableOpacity>
          </View>
        ) : (
          /* Course List */
          <View style={styles.courseList}>
            <View style={styles.sectionHeader}>
              <H3 style={styles.sectionTitle}>Your Courses</H3>
              <TouchableOpacity onPress={handleSyncNow} style={styles.portalLink}>
                <Caption style={styles.portalLinkText}>Open Portal</Caption>
                <Ionicons name="chevron-forward" size={12} color="#6366F1" />
              </TouchableOpacity>
            </View>

            <Caption style={styles.syncHint}>
              Tap a course to expand unit details. Pull down to refresh.
            </Caption>

            {summary.courses.map((course, idx) => (
              <CourseCard
                key={course.courseId || course.title || idx}
                course={course}
                downloadedTranscripts={downloadedTranscripts}
                onDownloadTranscript={handleDownloadTranscript}
                onOpenTranscript={handleOpenTranscript}
                onViewInPortal={handleViewInPortal}
              />
            ))}

            {/* Sync Prompt */}
            <View style={styles.syncPrompt}>
              <Ionicons name="information-circle-outline" size={14} color="#9CA3AF" />
              <Caption style={styles.syncPromptText}>
                Progress updates automatically when you visit the portal. Tap Sync to refresh now.
              </Caption>
            </View>
          </View>
        )}
      </ScrollView>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#F9FAFB',
  },
  header: {
    paddingBottom: 18,
    overflow: 'hidden',
  },
  aura1: {
    position: 'absolute',
    width: 180,
    height: 180,
    borderRadius: 90,
    backgroundColor: 'rgba(192,132,252,0.15)',
    top: -40,
    right: -30,
  },
  aura2: {
    position: 'absolute',
    width: 140,
    height: 140,
    borderRadius: 70,
    backgroundColor: 'rgba(99,102,241,0.2)',
    bottom: -30,
    left: -20,
  },
  headerContent: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 20,
    paddingTop: 14,
    paddingBottom: 16,
    gap: 14,
  },
  backBtn: {
    width: 36,
    height: 36,
    borderRadius: 18,
    backgroundColor: 'rgba(255,255,255,0.15)',
    alignItems: 'center',
    justifyContent: 'center',
  },
  headerSuper: {
    color: 'rgba(255,255,255,0.7)',
    fontSize: 9,
    fontWeight: '800',
    letterSpacing: 1.5,
  },
  headerTitle: {
    color: '#FFFFFF',
    fontSize: 18,
    fontWeight: '800',
    marginTop: 1,
  },
  syncBtn: {
    marginLeft: 'auto',
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    backgroundColor: 'rgba(255,255,255,0.15)',
    paddingHorizontal: 10,
    paddingVertical: 7,
    borderRadius: 10,
  },
  syncText: {
    color: '#FFFFFF',
    fontWeight: '800',
    fontSize: 10,
  },
  statsRow: {
    flexDirection: 'row',
    alignItems: 'center',
    marginHorizontal: 20,
    backgroundColor: 'rgba(255,255,255,0.12)',
    borderRadius: 14,
    paddingVertical: 12,
    marginBottom: 10,
  },
  statPill: {
    flex: 1,
    alignItems: 'center',
  },
  statDivider: {
    width: 1,
    height: 32,
    backgroundColor: 'rgba(255,255,255,0.2)',
  },
  statNum: {
    color: '#FFFFFF',
    fontSize: 22,
    fontWeight: '800',
  },
  statLabel: {
    color: 'rgba(255,255,255,0.7)',
    fontSize: 10,
    fontWeight: '600',
    marginTop: 1,
  },
  syncRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    paddingHorizontal: 22,
  },
  syncTimestamp: {
    color: 'rgba(255,255,255,0.55)',
    fontSize: 9,
  },
  scroll: {
    flex: 1,
  },
  scrollContent: {
    paddingTop: 20,
    paddingHorizontal: 18,
  },
  loadingState: {
    alignItems: 'center',
    paddingTop: 60,
    gap: 12,
  },
  loadingText: {
    color: '#9CA3AF',
  },
  emptyState: {
    alignItems: 'center',
    paddingTop: 40,
    paddingHorizontal: 24,
    gap: 14,
  },
  emptyIcon: {
    width: 80,
    height: 80,
    borderRadius: 24,
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: 6,
  },
  emptyTitle: {
    color: '#111827',
    fontWeight: '800',
    textAlign: 'center',
  },
  emptyDesc: {
    color: '#6B7280',
    textAlign: 'center',
    lineHeight: 22,
    fontSize: 13,
  },
  emptyBtn: {
    marginTop: 8,
    borderRadius: 14,
    overflow: 'hidden',
  },
  emptyBtnGrad: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    paddingHorizontal: 20,
    paddingVertical: 12,
  },
  emptyBtnText: {
    color: '#FFFFFF',
    fontWeight: '800',
    fontSize: 12,
  },
  sectionHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 6,
  },
  sectionTitle: {
    color: '#111827',
    fontWeight: '800',
    fontSize: 16,
  },
  portalLink: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 2,
  },
  portalLinkText: {
    color: '#6366F1',
    fontWeight: '700',
    fontSize: 11,
  },
  syncHint: {
    color: '#9CA3AF',
    fontSize: 11,
    marginBottom: 14,
  },
  courseList: {
    flex: 1,
  },
  syncPrompt: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: 7,
    marginTop: 6,
    padding: 14,
    backgroundColor: '#F9FAFB',
    borderRadius: 12,
    borderWidth: 1,
    borderColor: '#F3F4F6',
  },
  syncPromptText: {
    color: '#9CA3AF',
    fontSize: 11,
    flex: 1,
    lineHeight: 16,
  },
});

export default LMSProgressScreen;
